/* eslint-disable class-methods-use-this */
/* eslint-disable no-console */
/* eslint-disable no-restricted-syntax */
import { loadCKEditorCloud } from '@ckeditor/ckeditor5-react';
import { toast } from 'sonner';

import { AgentChatContextType } from '@/components/agent-chat/context/AgentChatContext';
import { ckeditorApi } from '@/services/api/ckeditorService';
import { ProposedEdit } from '@/services/types/ckeditor';
import { store } from '@/state/state';

import { RevisionHistoryAutosaveIntegration } from '../../integrations/revision-history-autosave-integration';
import { AgentChatUtilPlugin } from './agent-chat-util-plugin';
import { DiffPlugin } from './diff-plugin';

const {
  CKEditor: { Plugin, Editor: _Editor, Range: _Range },
  CKEditorPremiumFeatures: {
    RevisionHistory,
    RevisionTracker: _RevisionTracker,
    Revision: _Revision,
  },
} = await loadCKEditorCloud({
  version: '44.3.0',
  premium: true,
  translations: ['en'],
});

type Editor = InstanceType<typeof _Editor>;
type Range = InstanceType<typeof _Range>;
type RevisionTracker = InstanceType<typeof _RevisionTracker> & {
  currentRevision: InstanceType<typeof _Revision>;
};

interface AgentChatPluginConfig {
  documentId: string;
  matterId: string;
  agentChatContext: AgentChatContextType;
}

/**
 * Interface representing a pending change in the editor
 */

type PendingChangeStartOrEnd = {
  id: string;
  type: 'start' | 'end';
  newContent: string;
};

type PendingChangeReplace = {
  type: 'replace';
  id: string;
  oldContent: string;
  newContent: string;
};

export type PendingChange = PendingChangeStartOrEnd | PendingChangeReplace;

/**
 * AgentChatPlugin for CKEditor that adds functionality to interact with AI agents
 * for document editing and assistance.
 */
export class AgentChatPlugin extends Plugin {
  static get pluginName() {
    return 'AgentChatPlugin';
  }

  static get requires() {
    return [AgentChatUtilPlugin, RevisionHistory, DiffPlugin];
  }

  private pendingChanges: Set<string> = new Set();

  init() {
    // Plugin initialization
    const editor = this.editor as Editor;
    console.log('AgentChatPlugin initialized for editor:', editor);
  }

  /**
   * Adds a pending change to the collection and displays it in the document
   * @param pendingChange The pending change to add
   */
  public addPendingChangeReplace(pendingChange: PendingChangeReplace): void {
    const editor = this.editor as Editor;
    const utilPlugin = editor.plugins.get(AgentChatUtilPlugin);
    const trackChangesPlugin = editor.plugins.get('TrackChangesEditing');

    let oldRange: Range | null = null;
    // Find range for the old content
    try {
      oldRange = utilPlugin.createRangeForHTML(pendingChange.oldContent);
    } catch (error) {
      console.error('Error creating range for old content:', error);
      toast.error('Unable to automatically apply suggested edit.');
      return;
    }
    if (!oldRange) {
      toast.error('Unable to automatically apply suggested edit.');
      return;
    }

    if (this.pendingChanges.has(pendingChange.id)) {
      return;
    }

    this.pendingChanges.add(pendingChange.id);
    // Protect the old content range
    trackChangesPlugin.markDeletion(oldRange);

    editor.model.change((writer) => {
      const insertionPos = writer.createPositionAt(oldRange.end, 'after');

      // Create a selection at the insertion point
      const insertSelection = writer.createSelection(insertionPos);

      // Convert HTML to view fragment and then to model fragment
      const viewFragment = editor.data.processor.toView(
        pendingChange.newContent,
      );
      const modelFragment = editor.data.toModel(viewFragment);

      // Insert content
      const result = editor.model.insertContent(modelFragment, insertSelection);

      trackChangesPlugin.markInsertion(result);
    });
  }

  public addPendingChangeStartOrEnd(
    pendingChange: PendingChangeStartOrEnd,
  ): void {
    const editor = this.editor as Editor;
    const trackChangesPlugin = editor.plugins.get('TrackChangesEditing');

    const root = editor.model.document.getRoot();
    const viewFrag = editor.data.processor.toView(pendingChange.newContent);
    const modelFrag = editor.data.toModel(viewFrag);

    if (!root) {
      toast.error('Unable to automatically apply suggested edit.');
      return;
    }

    editor.model.change((writer) => {
      const insertionPos = writer.createPositionAt(
        root,
        pendingChange.type === 'start' ? 0 : 'end',
      );

      const result = editor.model.insertContent(modelFrag, insertionPos);
      trackChangesPlugin.markInsertion(result);
    });
  }

  public handleChatStarted() {
    const editor = this.editor as Editor;
    const tracker = editor.plugins.get(RevisionHistoryAutosaveIntegration);
    tracker.disableRevisionUpdates();
  }

  public async handleChatEnded() {
    const editor = this.editor as Editor;
    const tracker = editor.plugins.get(RevisionHistoryAutosaveIntegration);
    try {
      await this.checkForSuggestedEdits();
    } catch (error) {
      console.error('Failed to apply suggested edits:', error);
    } finally {
      tracker.enableRevisionUpdates();
    }
  }

  public async checkForSuggestedEdits() {
    const editor = this.editor as Editor;
    const tracker = editor.plugins.get('RevisionTracker') as RevisionTracker;
    const config = editor.config.get(
      'revisionHistoryIntegration',
    ) as AgentChatPluginConfig;

    const { documentId } = config;
    const proposedEdits = await store.dispatch(
      ckeditorApi.endpoints.listProposedEdits.initiate(
        {
          documentId,
          revisionId: tracker.currentRevision.id,
        },
        { forceRefetch: true },
      ),
    );

    if (!proposedEdits.data || proposedEdits.data.length === 0) {
      return;
    }

    this.applySuggestedEditBatch(proposedEdits.data);

    tracker.saveRevision();
  }

  private applySuggestedEditBatch(suggestedEdits: ProposedEdit[]) {
    for (const suggestedEdit of suggestedEdits) {
      try {
        if (suggestedEdit.type === 'replace') {
          this.addPendingChangeReplace({
            id: suggestedEdit.id,
            type: 'replace',
            oldContent: suggestedEdit.old_content,
            newContent: suggestedEdit.new_content,
          });
        } else if (suggestedEdit.type === 'start') {
          this.addPendingChangeStartOrEnd({
            id: suggestedEdit.id,
            type: 'start',
            newContent: suggestedEdit.new_content,
          });
        } else if (suggestedEdit.type === 'end') {
          this.addPendingChangeStartOrEnd({
            id: suggestedEdit.id,
            type: 'end',
            newContent: suggestedEdit.new_content,
          });
        } else if (suggestedEdit.type === 'full_edit') {
          this.handleFullEdit(suggestedEdit);
        }
      } catch (error) {
        console.error(
          `Failed to apply suggested edit ${suggestedEdit.id}:`,
          error,
        );
        toast.error('Unable to automatically apply a suggested edit.');
      }
    }
  }

  private handleFullEdit(suggestedEdit: ProposedEdit) {
    const editor = this.editor as Editor;
    const diffPlugin = editor.plugins.get(DiffPlugin);
    const trackChangesPlugin = editor.plugins.get('TrackChangesEditing');
    const changes = diffPlugin.diff(suggestedEdit.new_content);

    editor.model.change((writer) => {
      for (const change of changes) {
        if (change.type === 'insert') {
          const frag = editor.data.parse(change.html);
          const insRange = editor.model.insertContent(frag, change.position);
          trackChangesPlugin.markInsertion(insRange);
        } else if (change.type === 'delete') {
          trackChangesPlugin.markDeletion(change.range);
        }
      }
    });
  }
}
